import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { mergeRefs } from '@ipfabric/frontend-shared-assets/utils/react/mergeRefs';
import { type FC, type KeyboardEvent, useCallback, useState } from 'react';
import { useClickAway } from '../../hooks/useClickAway';
import { useStableCallback } from '../../hooks/useStableCallback';
import {
  ARROW_DOWN,
  ARROW_UP,
  ENTER,
  ESCAPE,
  SPACE_KEY,
} from '../../utils/keyboard/keyboardKeys';
import { ListDivider } from '../ListDivider/ListDivider';
import {
  type FloatingListProps,
  isFloatingListItemAnchor,
  isFloatingListItemContainer,
  isFloatingListItemDivider,
} from './FloatingList.types';
import { FloatingListItem } from './FloatingListItem';

export const FloatingList: FC<FloatingListProps> = ({
  items,
  size,
  children,
  initialSelectedIndex,
}) => {
  const defaultSelectedIndex =
    initialSelectedIndex !== undefined && initialSelectedIndex < items.length
      ? initialSelectedIndex
      : -1;

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setSelectedIndex(defaultSelectedIndex);
  }, [defaultSelectedIndex]);

  const floatingListRef = useClickAway<HTMLDivElement>((event) => {
    if (
      open &&
      refs.reference.current &&
      'contains' in refs.reference.current &&
      !refs.reference.current.contains(event.target as Node)
    ) {
      closeMenu();
    }
  });

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (open) => {
      if (open) {
        setOpen(true);
      } else {
        closeMenu();
      }
    },
    middleware: [
      flip({
        fallbackPlacements: ['top-end', 'top-start'],
      }),
      shift({ padding: 10 }),
      offset({
        mainAxis: 4,
        crossAxis: 4,
      }),
    ],
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
  });

  const moveSelectionDown = useStableCallback(() => {
    setSelectedIndex((selectedIndex) =>
      selectedIndex < items.length - 1 ? selectedIndex + 1 : items.length - 1,
    );
  });

  const moveSelectionUp = useStableCallback(() => {
    setSelectedIndex((selectedIndex) => {
      switch (selectedIndex) {
        case -1:
          return items.length - 1;
        case 0:
          return 0;
        default:
          return selectedIndex - 1;
      }
    });
  });

  /** Handles arrow selection and enter/space to confirm */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      e.preventDefault();

      switch (e.key) {
        case ARROW_DOWN:
          moveSelectionDown();
          break;
        case ARROW_UP:
          moveSelectionUp();
          break;
        case SPACE_KEY:
        case ENTER: {
          const selectedItem = items[selectedIndex];
          if (
            selectedItem === undefined ||
            isFloatingListItemDivider(selectedItem)
          ) {
            return;
          }

          if (selectedItem.disabled) {
            return;
          }

          if (isFloatingListItemAnchor(selectedItem)) {
            window.location.href = selectedItem.href;
          } else if (isFloatingListItemContainer(selectedItem)) {
            selectedItem.onClick();
          }

          closeMenu();
          break;
        }
        case ESCAPE:
          closeMenu();
          break;
      }
    },
    [selectedIndex, items, closeMenu],
  );

  return (
    <>
      {children(refs.setReference, setOpen, open)}
      <FloatingPortal>
        {open && (
          <FloatingFocusManager initialFocus={refs.floating} context={context}>
            <div
              className='z-floatingDropdown flex flex-col py-xs border border-b-default border-default bg-default rounded-sm drop-shadow focus-visible:outline-none'
              ref={mergeRefs(refs.setFloating, floatingListRef)}
              onKeyDown={handleKeyDown}
              style={floatingStyles}
            >
              {items.map((item, index) =>
                isFloatingListItemDivider(item) ? (
                  <ListDivider
                    key={`divider-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: divider does not has props for unique key
                      index
                    }`}
                  />
                ) : (
                  <FloatingListItem
                    {...item}
                    key={`item.label-${
                      // biome-ignore lint/suspicious/noArrayIndexKey: item can not have label prop for unique key
                      index
                    }`}
                    size={size}
                    selected={selectedIndex === index}
                    onClick={() => {
                      item.onClick?.();
                      if (!item.preventMenuClose) {
                        closeMenu();
                      }
                    }}
                  />
                ),
              )}
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  );
};
